/**
 * GitLab Job 日志查看对话框
 * 复用 Tekton 的布局：左侧 Job 列表，右侧日志查看器
 */

import { FC, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Grid,
  Paper,
  makeStyles,
  Theme,
  createStyles,
  Stepper,
  Step,
  StepButton,
  StepLabel,
  StepIconProps,
  CircularProgress,
  Typography,
  Box,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import Check from '@material-ui/icons/Check';
import Cancel from '@material-ui/icons/Cancel';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import { LogViewer } from '@backstage/core-components';
import { useApi, identityApiRef, discoveryApiRef } from '@backstage/core-plugin-api';
import type { GitLabJob } from './topology/gitlab-topology-utils';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500],
    },
    labelWrapper: {
      display: 'flex',
      flex: 1,
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    stepWrapper: {
      width: '100%',
    },
  }),
);

const useStepIconStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      color: theme.palette.text.disabled,
      display: 'flex',
      height: 22,
      alignItems: 'center',
    },
    completed: {
      color: theme.palette.status.ok,
    },
    error: {
      color: theme.palette.status.error,
    },
  }),
);

// Job Step 图标组件 - 只根据实际状态显示，不受选中状态影响
interface CustomStepIconProps extends StepIconProps {
  jobStatus?: string;
}

const JobStepIconComponent = ({ completed, error, jobStatus }: CustomStepIconProps) => {
  const classes = useStepIconStyles();

  const getMiddle = () => {
    // 只有当 Job 实际在运行时才显示 loading
    const isRunning = jobStatus?.toLowerCase() === 'running';
    
    if (isRunning) {
      return <CircularProgress size="24px" />;
    }
    if (completed) {
      return <Check />;
    }
    if (error) {
      return <Cancel />;
    }
    return <FiberManualRecordIcon />;
  };

  const getClassName = () => {
    if (completed) return classes.completed;
    if (error) return classes.error;
    return classes.root;
  };

  return (
    <div className={getClassName()}>
      {getMiddle()}
    </div>
  );
};

// Job 列表 Stepper - 复用 Tekton 的布局
interface JobStatusStepperProps {
  jobs: GitLabJob[];
  currentJobId: number | undefined;
  onJobChange: (id: number) => void;
}

const JobStatusStepper: FC<JobStatusStepperProps> = ({
  jobs,
  currentJobId,
  onJobChange,
}) => {
  const classes = useStyles();

  // 按照创建时间排序 - 从上到下执行顺序
  const sortedJobs = [...jobs].sort((a, b) => {
    return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
  });

  return (
    <div className={classes.root}>
      <Stepper
        activeStep={sortedJobs.findIndex(j => j.id === currentJobId)}
        orientation="vertical"
        nonLinear
      >
        {sortedJobs.map(job => {
          const isCompleted = ['success', 'failed', 'canceled', 'skipped'].includes(
            job.status?.toLowerCase(),
          );
          const isFailed = job.status?.toLowerCase() === 'failed';

          // 创建带 jobStatus 的图标组件
          const CustomIcon = (props: StepIconProps) => (
            <JobStepIconComponent {...props} jobStatus={job.status} />
          );

          return (
            <Step key={job.id} completed={isCompleted}>
              <StepButton onClick={() => onJobChange(job.id)}>
                <StepLabel
                  StepIconComponent={CustomIcon}
                  error={isFailed}
                >
                  <div className={classes.labelWrapper}>
                    <div className={classes.stepWrapper}>
                      <Typography variant="body2">{job.name}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {job.stage}
                      </Typography>
                    </div>
                    <Typography variant="caption">
                      {job.duration ? `${Math.round(job.duration)}s` : ''}
                    </Typography>
                  </div>
                </StepLabel>
              </StepButton>
            </Step>
          );
        })}
      </Stepper>
    </div>
  );
};

// 主对话框组件
interface GitLabJobLogDialogProps {
  open: boolean;
  onClose: () => void;
  jobs: GitLabJob[];
  pipelineName: string;
  projectPath: string;
  initialJobId?: number;
}

export const GitLabJobLogDialog: FC<GitLabJobLogDialogProps> = ({
  open,
  onClose,
  jobs,
  pipelineName,
  projectPath,
  initialJobId,
}) => {
  const classes = useStyles();
  const identityApi = useApi(identityApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const [currentJobId, setCurrentJobId] = useState<number | undefined>(initialJobId);
  const [log, setLog] = useState<string>('');
  const [loadingLog, setLoadingLog] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);

  // 设置初始 Job
  useEffect(() => {
    if (open && jobs.length > 0 && !currentJobId) {
      // 默认选择第一个 Job（排序后的第一个）
      setCurrentJobId(jobs[0]?.id);
    }
  }, [open, jobs, currentJobId]);

  // 获取 Job 日志
  useEffect(() => {
    const fetchLog = async () => {
      if (!currentJobId || !open) return;

      const currentJob = jobs.find(j => j.id === currentJobId);
      if (!currentJob) return;

      // 检查是否需要自动刷新（running 状态）
      const isRunning = currentJob.status?.toLowerCase() === 'running';
      setAutoRefresh(isRunning);

      setLoadingLog(true);
      try {
        const backendUrl = await discoveryApi.getBaseUrl('');
        const { token } = await identityApi.getCredentials();
        
        const response = await fetch(
          `${backendUrl}gitlab/rest/git.ekuaibao.com/projects/${encodeURIComponent(projectPath)}/jobs/${currentJobId}/trace`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          }
        );

        if (response.ok) {
          const logText = await response.text();
          setLog(logText || '暂无日志');
        } else {
          setLog(`获取日志失败 (HTTP ${response.status})`);
        }
      } catch (error) {
        setLog(`网络错误: ${error}`);
      } finally {
        setLoadingLog(false);
      }
    };

    fetchLog();
  }, [currentJobId, open, jobs, projectPath, identityApi, discoveryApi]);

  // 自动刷新日志（running 状态时）
  useEffect(() => {
    if (!autoRefresh || !open) return undefined;

    let interval: NodeJS.Timeout | undefined;
    
    // 启动自动刷新
    (async () => {
      const backendUrl = await discoveryApi.getBaseUrl('');
      
      interval = setInterval(async () => {
        if (!currentJobId) return;

        try {
          const { token } = await identityApi.getCredentials();
          const response = await fetch(
            `${backendUrl}gitlab/rest/git.ekuaibao.com/projects/${encodeURIComponent(projectPath)}/jobs/${currentJobId}/trace`,
            {
              headers: {
                'Authorization': `Bearer ${token}`,
              },
            }
          );

          if (response.ok) {
            const logText = await response.text();
            setLog(logText || '暂无日志');
          }
        } catch (error) {
          // 静默失败
        }
      }, 8000); // 每 8 秒刷新一次
    })();

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, open, currentJobId, projectPath, identityApi, discoveryApi]);

  return (
    <Dialog
      maxWidth="xl"
      fullWidth
      open={open}
      onClose={onClose}
    >
      <DialogTitle>
        Pipeline: {pipelineName}
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Grid container>
          {/* 左侧：Job 列表 - 复用 Tekton 的 Stepper 样式 */}
          <Grid item xs={3}>
            <Paper>
              <JobStatusStepper
                jobs={jobs}
                currentJobId={currentJobId}
                onJobChange={setCurrentJobId}
              />
            </Paper>
          </Grid>
          
          {/* 右侧：日志查看器 - 复用 Tekton 的布局 */}
          <Grid item xs={9}>
            <div style={{ height: '80vh' }}>
              {loadingLog ? (
                <Paper
                  elevation={1}
                  style={{ 
                    height: '100%', 
                    width: '100%', 
                    minHeight: '30rem',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <CircularProgress />
                </Paper>
              ) : (
                <Box style={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Paper
                    elevation={1}
                    style={{ flex: 1, minHeight: '30rem' }}
                  >
                    <LogViewer text={log} />
                  </Paper>
                  {/* 自动刷新提示 */}
                  {autoRefresh && (
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      style={{
                        padding: '8px',
                        backgroundColor: '#f5f5f5',
                        borderTop: '1px solid #e0e0e0',
                      }}
                    >
                      <CircularProgress size={16} style={{ marginRight: 8 }} />
                      <Typography variant="caption" color="textSecondary">
                        自动刷新中...（每 3 秒）
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </div>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default GitLabJobLogDialog;

